import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ContentScheduleModal } from '../ContentScheduleModal';
import { FullscreenVideoPlayer } from '@/components/FullscreenVideoPlayer';
import { DesktopInfoPanel } from './DesktopInfoPanel';
import { InlineVideoPlayer } from './InlineVideoPlayer';
import { ABVersionPanel } from './ABVersionPanel';
import type { ContentEvent, PublicationItem, SocialConnection } from './types';
import type { TimelineComment } from '@/types/video-validation';
import type { Channel } from '@/services/business/calendar';
import type { RefObject } from 'react';

interface UserProfile {
  first_name?: string | null;
  last_name?: string | null;
  avatar_url?: string | null;
}

interface DesktopLayoutProps {
  readonly event: ContentEvent;
  // Detail state
  readonly editingField: 'title' | 'description' | null;
  readonly setEditingField: (f: 'title' | 'description' | null) => void;
  readonly editTitle: string;
  readonly editDescription: string;
  readonly isReadOnly: boolean;
  readonly isValidated: boolean;
  readonly isValidating: boolean;
  readonly allDatesUnchecked: boolean;
  readonly allItems: PublicationItem[];
  readonly checkedDates: Record<string, boolean>;
  readonly scheduleModalOpen: boolean;
  readonly setScheduleModalOpen: (v: boolean) => void;
  readonly showCloseConfirm: boolean;
  readonly setShowCloseConfirm: (v: boolean) => void;
  readonly activeVideoUrl: string | null;
  readonly effectivePlatforms: SocialConnection[];
  readonly rawInlineVideoKeys: string[];
  readonly versionsEnabled: boolean[];
  readonly isAutoValidated: boolean;
  readonly userProfile: UserProfile | null;
  // Handlers from detail
  readonly onSaveTitle: (val: string) => void;
  readonly onSaveDescription: (val: string) => void;
  readonly onValidate: () => void;
  readonly onToggleDate: (key: string) => void;
  readonly onSaveSchedule: (pubs: Array<{ channel: Channel; date: Date; time: string }>) => Promise<boolean>;
  readonly onValidateAndLaunch: (pubs: Array<{ channel: Channel; date: Date; time: string }>) => Promise<void>;
  readonly handleConfirmClose: () => void;
  readonly handleCloseVideo: () => void;
  // Video player state
  readonly videoRef: RefObject<HTMLVideoElement>;
  readonly inlineActiveUrl: string | null;
  readonly inlinePlaying: boolean;
  readonly setInlinePlaying: (v: boolean) => void;
  readonly inlineMuted: boolean;
  readonly inlineCurrentTime: number;
  readonly setInlineCurrentTime: (v: number) => void;
  readonly inlineDuration: number;
  readonly setInlineDuration: (v: number) => void;
  readonly inlineActiveVersion: number;
  readonly setInlineActiveVersion: (v: number) => void;
  readonly onTogglePlay: () => void;
  readonly onToggleMute: () => void;
  readonly onSeek: (time: number) => void;
  readonly formatTime: (s: number) => string;
  readonly onToggleVersionEnabled: (i: number) => void;
  // Feedback state
  readonly feedbackComments: TimelineComment[];
  readonly isSendingFeedback: boolean;
  readonly focusCommentId: string | null;
  readonly activeMarkerId: string | null;
  readonly showFeedbackPanel: boolean;
  readonly setShowFeedbackPanel: (v: boolean) => void;
  readonly setFocusCommentId: (id: string | null) => void;
  readonly setActiveMarkerId: (id: string | null) => void;
  readonly onOpenFeedbackPanel: () => void;
  readonly onSendFeedback: () => void;
  readonly onCommentsChange: (updated: TimelineComment[]) => void;
  readonly onMarkerClick: (comment: TimelineComment) => void;
}

export function DesktopLayout({
  event,
  editingField,
  setEditingField,
  editTitle,
  editDescription,
  isReadOnly,
  isValidated,
  isValidating,
  allDatesUnchecked,
  allItems,
  checkedDates,
  scheduleModalOpen,
  setScheduleModalOpen,
  showCloseConfirm,
  setShowCloseConfirm,
  activeVideoUrl,
  effectivePlatforms,
  rawInlineVideoKeys,
  versionsEnabled,
  isAutoValidated,
  userProfile,
  onSaveTitle,
  onSaveDescription,
  onValidate,
  onToggleDate,
  onSaveSchedule,
  onValidateAndLaunch,
  handleConfirmClose,
  handleCloseVideo,
  videoRef,
  inlineActiveUrl,
  inlinePlaying,
  setInlinePlaying,
  inlineMuted,
  inlineCurrentTime,
  setInlineCurrentTime,
  inlineDuration,
  setInlineDuration,
  inlineActiveVersion,
  setInlineActiveVersion,
  onTogglePlay,
  onToggleMute,
  onSeek,
  formatTime,
  onToggleVersionEnabled,
  feedbackComments,
  isSendingFeedback,
  focusCommentId,
  activeMarkerId,
  showFeedbackPanel,
  setShowFeedbackPanel,
  setFocusCommentId,
  setActiveMarkerId,
  onOpenFeedbackPanel,
  onSendFeedback,
  onCommentsChange,
  onMarkerClick,
}: DesktopLayoutProps) {
  return (
    <TooltipProvider>
      <div
        className="flex items-center justify-center w-full h-full gap-8 p-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {/* LEFT PANEL */}
        <DesktopInfoPanel
          event={event}
          editingField={editingField}
          setEditingField={setEditingField}
          editTitle={editTitle}
          editDescription={editDescription}
          isReadOnly={isReadOnly}
          isValidated={isValidated}
          isValidating={isValidating}
          allDatesUnchecked={allDatesUnchecked}
          allItems={allItems}
          checkedDates={checkedDates}
          showFeedbackPanel={showFeedbackPanel}
          setShowFeedbackPanel={setShowFeedbackPanel}
          feedbackComments={feedbackComments}
          isSendingFeedback={isSendingFeedback}
          focusCommentId={focusCommentId}
          activeMarkerId={activeMarkerId}
          inlineActiveVersion={inlineActiveVersion}
          inlineCurrentTime={inlineCurrentTime}
          inlineDuration={inlineDuration}
          inlinePlaying={inlinePlaying}
          userProfile={userProfile}
          isAutoValidated={isAutoValidated}
          onSaveTitle={onSaveTitle}
          onSaveDescription={onSaveDescription}
          onValidate={onValidate}
          onToggleDate={onToggleDate}
          onOpenSchedule={() => setScheduleModalOpen(true)}
          onOpenFeedbackPanel={onOpenFeedbackPanel}
          onSeekTo={onSeek}
          onTogglePlay={onTogglePlay}
          onSendFeedback={onSendFeedback}
          onCommentsChange={onCommentsChange}
          onCommentAdded={(id) => setActiveMarkerId(id)}
          setFocusCommentId={setFocusCommentId}
        />

        {/* CENTER — Video Player */}
        <div
          className="flex-shrink-0 h-full max-h-[min(90vh,576px)]"
          style={{ aspectRatio: '9/16' }}
        >
          <InlineVideoPlayer
            videoRef={videoRef}
            videoUrl={inlineActiveUrl}
            isPlaying={inlinePlaying}
            isMuted={inlineMuted}
            currentTime={inlineCurrentTime}
            duration={inlineDuration}
            isValidated={isValidated}
            comments={feedbackComments}
            activeMarkerId={activeMarkerId}
            onTogglePlay={onTogglePlay}
            onToggleMute={onToggleMute}
            onSeek={onSeek}
            onMarkerClick={onMarkerClick}
            onTimeUpdate={() => {
              const v = videoRef.current;
              if (v) setInlineCurrentTime(v.currentTime);
            }}
            onLoadedMetadata={() => {
              const v = videoRef.current;
              if (v) setInlineDuration(v.duration);
            }}
            onPlay={() => setInlinePlaying(true)}
            onPause={() => setInlinePlaying(false)}
            formatTime={formatTime}
          />
        </div>

        {/* RIGHT — A/B version panel (only for true multihook) */}
        <ABVersionPanel
          videoKeys={rawInlineVideoKeys}
          activeVersion={inlineActiveVersion}
          versionsEnabled={versionsEnabled}
          isReadOnly={isReadOnly}
          onVersionChange={setInlineActiveVersion}
          onVersionToggle={onToggleVersionEnabled}
        />
      </div>

      {/* Shared modals — rendered once here, not duplicated */}
      <ContentScheduleModal
        isOpen={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        contentTitle={event.title}
        publications={event.publicationTimes ?? []}
        connectedPlatforms={effectivePlatforms}
        onSave={onSaveSchedule}
        onValidateAndLaunch={onValidateAndLaunch}
        isMultihook={event.isMultihook}
      />

      {activeVideoUrl && (
        <FullscreenVideoPlayer
          videoId={event.id}
          videoSrc={activeVideoUrl}
          onClose={handleCloseVideo}
          currentUserName={
            userProfile
              ? `${userProfile.first_name ?? ''} ${userProfile.last_name ?? ''}`.trim() ||
                'Utilisateur'
              : 'Utilisateur'
          }
          currentUserAvatar={userProfile?.avatar_url ?? undefined}
        />
      )}

      <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <AlertDialogContent className="bg-black/95 border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Modifications non enregistrées</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              Vos modifications ne seront pas enregistrées. Êtes-vous sûr ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 text-white border-white/10 hover:bg-white/10">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmClose}
              className="bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30"
            >
              Fermer sans enregistrer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
}
